<template>
  <div class="automation-editor">
    <!-- HEADER -->
    <header class="editor-header">
      <div class="header-content">
        <!-- Left Section -->
        <div class="header-left">
          <!-- Back Button -->
          <el-button class="back-btn" type="text" size="small" @click="cancel" :disabled="loading">
            <i class="el-icon-arrow-left">Back</i>
          </el-button>

          <!-- Title -->
          <h2 class="editor-title">{{ currentEntity.name }} - {{ trigger.name }}</h2>
        </div>

        <!-- Right Section -->
        <div class="header-right">
          <el-button
            class="ai-request-btn"
            size="small"
            type="primary"
            plain
            @click="requestAiHelp"
            :disabled="loading"
          >
            <i class="el-icon-magic-stick"></i>
            Ask AI
          </el-button>
          <el-button
            size="small"
            type="primary"
            @click="saveAutomation"
            :loading="loading"
            :disabled="loading"
          >
            <template v-if="loading">
              <i class="el-icon-loading"></i>
              {{ loadingText }}
            </template>
            <template v-else>Save</template>
          </el-button>
        </div>
      </div>
    </header>

    <!-- MAIN CONTENT -->
    <main class="editor-content" :class="{ 'loading-overlay': loading }">
      <div v-if="loading" class="loading-indicator">
        <el-progress :percentage="100" status="success" :show-text="false" :indeterminate="true" />
        <p class="loading-text">{{ loadingText }}</p>
      </div>

      <div class="automation-flow" :class="{ 'content-blur': loading }">
        <!-- TRIGGER SECTION -->
        <section class="configuration-section">
          <div class="section-header">
            <h3>Trigger</h3>
            <el-button
              class="settings-trigger-btn"
              type="text"
              @click="openTriggerSettings"
              :disabled="loading"
            >
              <i class="el-icon-setting"></i>
              Configure
            </el-button>
          </div>
          <div class="section-content">
            <div class="trigger-card">
              <div class="card-header">
                <div class="card-icon">⚡</div>
                <div class="header-content">
                  <h4>{{ getTriggerDescription(trigger.when) }}</h4>
                </div>
              </div>
              <div class="card-body">
                <p>{{ trigger.description }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- PLUS BUTTON BETWEEN TRIGGER & ACTIONS -->
        <div class="add-block-btn" @click="addAction" :class="{ disabled: loading }">+</div>

        <!-- ACTIONS SECTION -->
        <section
          class="configuration-section actions-section"
          :class="{ 'full-screen': isActionsFullScreen }"
        >
          <div class="section-header">
            <h3>Actions</h3>
            <div class="section-actions">
              <el-button
                class="full-screen-toggle"
                type="text"
                @click="toggleActionsFullScreen"
                :icon="isActionsFullScreen ? 'el-icon-close' : 'el-icon-full-screen'"
                :disabled="loading"
              >
                {{ isActionsFullScreen ? 'Exit Full Screen' : 'Full Screen' }}
              </el-button>
            </div>
          </div>
          <div
            class="section-content actions-scroll"
            v-if="!['', undefined, null].includes(entity)"
          >
            <ActionBlock
              v-for="(block, index) in actions"
              :key="index"
              :block="block"
              :actionNumber="index + 1"
              :trigger="trigger"
              :currentEntity="entity"
              :allEntities="allEntities"
              :allEntityFilters="allEntityFilters"
              :entityAllFilters="entityAllFilters"
              :allFields="allFields"
              :allContacts="allContacts"
              :companyUsers="companyUsers"
              :allTemplates="allTemplates"
              :templateFieldMap="templateFieldMap"
              :allTemplateFieldMap="allTemplateFieldMap"
              :companyAllTemplateFields="companyAllTemplateFields"
              :allFormbuilders="allFormbuilders"
              :entityRelationships="entityRelationships"
              :randomStr="randomStr"
              @remove="actions.splice(index, 1)"
              :disabled="loading"
            />
          </div>
        </section>

        <!-- PLUS BUTTON BETWEEN ACTIONS & END -->
        <div class="add-block-btn" @click="addAction" :class="{ disabled: loading }">+</div>

        <!-- END SECTION -->
        <section class="configuration-section">
          <div class="section-header">
            <h3>End</h3>
          </div>
          <div class="section-content end-block">
            <p>This automation completes here.</p>
          </div>
        </section>
      </div>
    </main>

    <!-- TRIGGER SETTINGS DRAWER -->
    <el-drawer
      title="Trigger Settings"
      :visible.sync="showTriggerSettings"
      direction="rtl"
      :size="getIsMobile ? '80%' : '50%'"
      class="trigger-settings-drawer"
      :close-on-press-escape="!loading"
      :show-close="!loading"
    >
      <div class="p-4">
        <TriggerSettings
          v-if="entity && showTriggerSettings"
          :trigger="trigger"
          :currentEntity="entity"
          @update="updateTrigger"
          @close="showTriggerSettings = false"
          :disabled="loading"
        />
      </div>
    </el-drawer>
  </div>
</template>

<script>
  import ActionBlock from './ActionComponent.vue';
  import TriggerSettings from './TriggerSettings.vue';
  import { mapGetters } from 'vuex';
  import AutomationHelper from '../../../mixins/AutomationHelper';
  import templateBuilderHelper from '../../../mixins/templateBuilderHelper';
  import { postAPICall } from '../../../helpers/httpHelper';
  import { fetchEntityRelationships } from '../../../repo/entityRelationshipsRepo';

  export default {
    name: 'AutomationEditor',
    mixins: [AutomationHelper, templateBuilderHelper],
    props: {
      currentEntity: {
        type: Object,
      },
      allEntities: {
        type: Array,
      },
      allEntityFilters: {
        type: Object,
      },
      entityAllFilters: {
        type: Array,
      },
      entity_id: {
        type: String,
      },
      automationData: {
        type: Object,
      },
      allContacts: Array,
      companyUsers: Array,
      allTemplates: Array,
      templateFieldMap: Object,
      allTemplateFieldMap: Object,
      companyAllTemplateFields: Object,
      allFormbuilders: Array,
    },
    data() {
      return {
        trigger: {
          name: '',
          description: '',
          when: 'ADDED',
        },
        actions: [],
        allFields: [],
        isActionsFullScreen: false,
        showTriggerSettings: false,
        entity: null,
        loading: false,
        entityRelationships: [],
      };
    },
    created() {
      if (this.automationData) {
        this.trigger = {
          ...this.automationData,
        };
        this.actions = this.automationData.then || [];
      }
    },
    async mounted() {
      this.entity = this.currentEntity;
      this.allFields = this.getAllFieldsFromEntity(this.currentEntity, true, false, true);
      if (!this.trigger.entity_id) {
        this.trigger.entity_id = this.entity_id;
      }
      this.entityRelationships = await fetchEntityRelationships({
        entity_id: this.currentEntity._id,
      });
    },
    components: {
      ActionBlock,
      TriggerSettings,
    },
    computed: {
      ...mapGetters('navigationOpen', ['getIsMobile', 'getIsRSOpen']),
      loadingText() {
        return this.trigger._id ? 'Updating Automation...' : 'Creating Automation...';
      },
      isUpdate() {
        return !!this.trigger._id;
      },
    },
    methods: {
      getTriggerDescription(type) {
        const triggerMessages = {
          ADDED: 'Perform when a new record is added to the system',
          NOTIFICATION: 'Perform when a notification is sent',
          DELETED: 'Perform when a record is deleted',
          TRANSACTION: 'Perform when a payment transaction is recorded',
          AUTOMATION: 'Perform on a recurring schedule automatically',
          FILE_ADDED: 'Perform when a new file is uploaded or attached',
          DATE_TIME: 'Perform based on date/time field conditions',
          template: 'Perform when a template is updated or modified',
        };

        if (type && type.startsWith('template/')) {
          return triggerMessages.template;
        }

        return triggerMessages[type] || 'Perform when the trigger condition is met';
      },
      goBack() {
        this.isEdit = false;
      },
      randomStr(len = 10) {
        return Math.random()
          .toString(36)
          .slice(2, 2 + len);
      },
      addAction() {
        if (this.loading) return;
        this.actions.push({
          keyIndex: this.randomStr(10),
        });
      },
      requestAiHelp() {
        if (this.loading) return;
        this.$message('AI assistant feature coming soon!');
      },
      cancel() {
        if (this.loading) return;
        this.$emit('cancel');
      },
      async saveAutomation() {
        try {
          if (this.loading) return;

          if (!this.trigger.name?.trim()) {
            this.$message.error('Trigger name is required.');
            this.openTriggerSettings();
            return;
          }
          if (!this.actions?.length) {
            return this.$message.error('At least one action is required.');
          }

          const params = {
            ...this.trigger,
            then: this.actions,
          };

          this.loading = true;

          const isUpdate = !!this.trigger._id;
          const method = isUpdate ? 'PATCH' : 'POST';
          const endpoint = isUpdate
            ? `automation-pipeline/update/${this.trigger._id}`
            : `automation-pipeline/create`;

          await postAPICall(method, endpoint, params);

          this.$message.success(
            isUpdate ? 'Automation updated successfully' : 'Automation created successfully'
          );
          this.$emit('saved');
        } catch (error) {
          console.error('Error saving automation:', error);
          this.$message.error('Failed to save automation. Please try again.');
        } finally {
          this.loading = false;
        }
      },
      toggleActionsFullScreen() {
        if (this.loading) return;
        this.isActionsFullScreen = !this.isActionsFullScreen;
      },
      openTriggerSettings() {
        if (this.loading) return;
        this.showTriggerSettings = true;
      },
      updateTrigger(updatedTrigger) {
        if (this.loading) return;
        this.trigger = { ...this.trigger, ...updatedTrigger };
        this.showTriggerSettings = false;
        this.$message.success('Trigger settings updated!');
      },
    },
  };
</script>

<style lang="scss" scoped>
  .automation-editor {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: #f7f8fa;
    font-family: 'Inter', 'Segoe UI', sans-serif;
    position: relative;
  }

  /* HEADER */
  .editor-header {
    background: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 24px;
      max-width: 1200px;
      margin: 0 auto;

      .header-left {
        display: flex;
        align-items: center;
        gap: 12px;

        .editor-title {
          font-weight: 600;
          font-size: 18px;
          color: #111827;
          margin: 0;
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 10px;

        .el-button {
          font-weight: 500;
          border-radius: 6px;
          padding: 0 14px;
          height: 32px;

          &[disabled] {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }

        .ai-request-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #2563eb;
          border-color: #bfdbfe;

          i {
            font-size: 14px;
          }

          &:hover {
            background: #eff6ff;
            border-color: #3b82f6;
            color: #1d4ed8;
          }
        }
      }
    }
  }

  /* MAIN CONTENT */
  .editor-content {
    flex: 1;
    padding: 32px 16px;
    display: flex;
    justify-content: center;
    overflow-y: auto;
    position: relative;

    &.loading-overlay {
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        z-index: 10;
        pointer-events: none;
      }
    }
  }

  .loading-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 20;
    text-align: center;
    background: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    min-width: 200px;

    .loading-text {
      margin-top: 12px;
      color: #64748b;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .content-blur {
    filter: blur(2px);
    pointer-events: none;
  }

  .automation-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 1000px;
    transition: filter 0.3s ease;
  }

  /* CONFIGURATION SECTIONS */
  .configuration-section {
    width: 100%;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
    transition:
      box-shadow 0.2s ease,
      opacity 0.3s ease;

    &:hover {
      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
    }

    .section-header {
      padding: 16px 20px;
      border-bottom: 1px solid #f1f1f1;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
      }
    }

    .section-content {
      padding: 16px 20px;
      max-height: 280px;
      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }
    }
  }

  /* ACTION CARD */
  .action-card,
  .trigger-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
    transition: all 0.2s ease;

    &:hover {
      background: #fff;
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
    }

    .card-header {
      display: flex;
      align-items: center;
      gap: 10px;
      background: #f3f4f6;
      padding: 12px 16px;

      .card-icon {
        width: 36px;
        height: 36px;
        background: #3b82f6;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        font-size: 18px;
      }

      h4 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
      }
    }

    .card-body {
      padding: 12px 16px;
      font-size: 14px;
      color: #475569;
    }
  }

  /* PLUS BUTTON */
  .add-block-btn {
    width: 44px;
    height: 44px;
    background: #fff;
    border: 2px dashed #cbd5e1;
    border-radius: 50%;
    font-size: 24px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(.disabled) {
      border-color: #3b82f6;
      color: #3b82f6;
      box-shadow: 0 0 0 3px #eff6ff;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      border-color: #e5e7eb;
      color: #9ca3af;
    }
  }

  /* FULL SCREEN STYLES */
  .actions-section.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1000;
    border-radius: 0;
    border: none;
    box-shadow: none;
    display: flex;
    flex-direction: column;

    .section-header {
      padding: 20px 24px;
      border-bottom: 1px solid #e5e7eb;
      background: #fff;

      h3 {
        font-size: 18px;
      }
    }

    .section-content {
      flex: 1;
      max-height: none;
      padding: 24px;
      overflow-y: auto;
      background: #f7f8fa;
    }
    .section-content > * {
      width: 100%;
    }
  }

  .full-screen-toggle {
    color: #3b82f6;

    &:hover {
      color: #2563eb;
    }
  }

  /* Adjust other sections when actions are in full screen */
  .actions-section.full-screen ~ * {
    opacity: 0;
    pointer-events: none;
  }

  /* SETTINGS BUTTONS */
  .settings-trigger-btn {
    color: #6b7280;
    font-size: 14px;

    &:hover:not([disabled]) {
      color: #3b82f6;
    }

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .settings-btn {
    border-color: #d1d5db;
    color: #374151;

    &:hover {
      border-color: #3b82f6;
      color: #3b82f6;
    }
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;

    .back-btn {
      color: #374151;
      padding: 0 6px;
      font-size: 16px;

      i {
        font-size: 18px;
      }

      &:hover:not([disabled]) {
        color: #2563eb;
        background: #eff6ff;
      }

      &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .editor-title {
      font-weight: 600;
      font-size: 18px;
      color: #111827;
      margin: 0;
    }
  }

  .trigger-card {
    @apply bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow;
  }

  .card-header {
    @apply flex items-start gap-3 mb-3;
  }

  .header-content {
    @apply flex-grow;
  }

  .card-header h4 {
    @apply text-lg font-semibold text-gray-800 mb-1;
  }

  .helper-text {
    @apply text-xs text-gray-500 leading-relaxed;
  }

  .card-body {
    @apply text-sm text-gray-600;
  }
</style>
